import React, {useEffect, useState} from 'react';
import {message, Modal, Tooltip} from "antd";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    StatusBadge,
} from "../../components/ui";

const statusTones = {
    process: 'blue',
    accept: 'green',
    cancel: 'red',
};

function ResendRequests(props) {
    const { t } = useTranslation();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [requests, setRequests] = useState([])
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [actionType, setActionType] = useState(null); // To distinguish between accept and cancel
    const [description, setDescription] = useState(''); // For storing the description input
    const [isDescriptionModalVisibleForAddComment, setIsDescriptionModalVisibleForAddComment] = useState(false);
    const [isDescriptionModalVisible, setIsDescriptionModalVisible] = useState(false);
    const [fullDescription, setFullDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const openDescriptionModal = (desc) => {
        setFullDescription(desc);
        setIsDescriptionModalVisible(true);
    };
    const openModal = (request) => {
        setSelectedRequest(request);
        setIsModalVisible(true);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setSelectedRequest(null);
    };

    const getRequests = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/financiers/return_warehouses?page=${page}&pageSize=${pageSize}`);
            const { data, meta } = response.data;

            setRequests(data);
            setPagination({
                current: meta.current_page,
                pageSize: meta.per_page,
                total: meta.total,
            });
        } catch (error) {
            message.error(t('store.fetchError'));
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        getRequests();
    }, []);

    const handleTableChange = (pagination) => {
        getRequests(pagination.current, pagination.pageSize);
    };

    const renderStatusTag = (status) => (
        <StatusBadge tone={statusTones[status] ?? 'muted'}>{t(`status.${status}`, { defaultValue: t('common.unknown') })}</StatusBadge>
    );

    const linkButton = (label, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className="text-left text-sm font-medium text-[#1D2C7B] hover:underline"
        >
            {label}
        </button>
    );

    const handleActionClick = (applicationId, status) => {
        setSelectedRequest({applicationId});
        setActionType(status); // Set the actionType as 'accept' or 'cancel'
        setIsDescriptionModalVisibleForAddComment(true); // Open the description modal
    };

    const handleFinalAction = async () => {

        const payload = {
            status: actionType,
        };

        try {
            const response = await axiosInstance.post(`/financiers/check/${selectedRequest.applicationId}/return_warehouse`, payload);
            toast.success(t('store.returnedOk'));
            setIsDescriptionModalVisibleForAddComment(false);
            setIsModalVisible(false);
            setDescription('');
        } catch (error) {
            console.error(`Error setting request to ${actionType}:`, error);
            message.error(`Error setting request to ${actionType}`);
        }

        getRequests(); // Refresh the table after action
    };

    const formatDate = (text) => new Date(text).toISOString().slice(0, 19).replace('T', ' '); // Преобразование в "2024-11-23 12:36:28"

    const rowActions = (record) => (
        record.status !== 'accept' && record.status !== 'cancel' ? (
            <div className="flex items-center gap-0.5">
                <Tooltip title={t('store.accept')}>
                    <RowIconButton
                        className="hover:!text-emerald-600"
                        onClick={() => handleActionClick(record.id, 'accept')}
                    >
                        <CheckCircleIcon className="w-[18px] h-[18px]"/>
                    </RowIconButton>
                </Tooltip>
                <Tooltip title={t('store.reject')}>
                    <RowIconButton
                        className="hover:!text-red-600"
                        onClick={() => handleActionClick(record.id, 'cancel')}
                    >
                        <XCircleIcon className="w-[18px] h-[18px]"/>
                    </RowIconButton>
                </Tooltip>
            </div>
        ) : null
    );

    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'id',
            key: 'id',
            width: 72,
            render: (id) => <span className="text-xs text-slate-500 tabular-nums">{id}</span>,
        },
        {
            title: t('common.qty'),
            dataIndex: 'qty',
            key: 'qty',
            width: 90,
            render: (qty) => <span className="text-sm text-slate-700 tabular-nums">{qty}</span>,
        },
        {
            title: t('common.date'),
            width: 180,
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{formatDate(text)}</span>
            ),
        },
        {
            title: t('store.sender'),
            dataIndex: 'sender',
            key: 'sender',
            render: (sender) => linkButton(sender?.name, () => openModal(sender))
        },
        {
            title: t('store.warehouse'),
            dataIndex: 'warehouse',
            key: 'warehouse',
            render: (warehouse) => linkButton(warehouse?.name, () => openModal(warehouse))
        },
        {
            title: t('store.confirmer'),
            dataIndex: 'receive',
            width: 180,
            key: 'receive',
            render: (receive) => (
                receive
                    ? linkButton(receive.name, () => openModal(receive))
                    : <StatusBadge tone="muted">{t('store.notConfirmed')}</StatusBadge>
            )
        },
        {
            title: t('common.status'),
            dataIndex: 'status',
            width: 170,
            key: 'status',
            render: (status) => renderStatusTag(status)
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 96,
            render: (_, record) => rowActions(record)
        }
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    {linkButton(record.warehouse?.name, () => openModal(record.warehouse))}
                    <span className="block text-xs text-slate-500 mt-0.5">{record.sender?.name}</span>
                </div>
                {rowActions(record)}
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                {renderStatusTag(record.status)}
                <span className="tabular-nums">{record.qty}</span>
                <span className="tabular-nums">{formatDate(record.created_at)}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar title={t('store.resend')} count={pagination.total}/>

                <DataTable
                    loading={loading}
                    columns={columns}
                    dataSource={requests}
                    rowKey="id"
                    scrollX={1100}
                    mobileCard={renderMobileCard}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    onChange={handleTableChange}/>
            </PageCard>

            {/* Modal for showing details */}
            <Modal
                title={t('store.details')}
                open={isModalVisible}
                onCancel={closeModal}
                footer={null}
                width="100%"
                style={{maxWidth: 520}}
            >
                {selectedRequest && (
                    <div className="text-sm text-slate-700">
                        <p><strong>{t('store.name')}:</strong> {selectedRequest?.name}</p>
                        {
                            selectedRequest?.phone && (
                                <p>
                                    <strong>{t('store.phoneNumber')}:</strong>{' '}
                                    <a href={`tel:+${selectedRequest?.phone}`} className="text-[#1D2C7B] tabular-nums">
                                        {selectedRequest?.phone}
                                    </a>
                                </p>
                            )
                        }
                        {selectedRequest?.category && (
                            <>
                                <p><strong>{t('common.category')}:</strong> {selectedRequest?.category.name}</p>
                                {/*<p><strong>Миқдор:</strong> {selectedRequest?.qty}</p>*/}
                                {/*<p><strong>Нархи:</strong> {selectedRequest?.price}</p>*/}
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* Modal for entering description */}
            <Modal
                title={actionType === 'accept' ? t('store.commentForAccept') : t('store.commentForReject')}
                open={isDescriptionModalVisibleForAddComment}
                onCancel={() => setIsDescriptionModalVisibleForAddComment(false)}
                onOk={handleFinalAction}
                okText={actionType === 'accept' ? t('store.accept') : t('store.reject')}
                width="100%"
                style={{maxWidth: 520}}
            >
                <p className="text-sm text-slate-700">
                    {actionType === 'accept' ? t('store.confirmAccept') : t('store.confirmReject')}
                </p>
            </Modal>

            <Modal
                title={t('store.fullRemark')}
                open={isDescriptionModalVisible}
                onCancel={() => setIsDescriptionModalVisible(false)}
                footer={null}
                width="100%"
                style={{maxWidth: 520}}
            >
                <p className="text-sm text-slate-700">{fullDescription}</p>
            </Modal>

        </PageShell>
    );
}

export default ResendRequests;
